import { ResponsivePie } from "@nivo/pie";
import { DEFAULT_CURRENCY_SYMBOL } from "@zenith/data/constants";
import {
  BarChartComponent,
  EmptyChartCardComponent,
} from "@zenith/features/Analytics";
import {
  BeneficiaryChartDTO,
  SendMoneyChartDTO,
} from "@zenith/services/transactions";
import { getChartEntryColor, getSuffixedNumber, formatCurrency } from "@zenith/utils";
import { memo } from "react";

export interface SendMoneyBeneficiariesComponentProps {
  displayBeneficiaryData: BeneficiaryChartDTO[] | undefined;
  displaySendMoneyData: SendMoneyChartDTO[] | undefined;
}

interface PieDataProps {
  id: string;
  label: string;
  value: number;
  formattedValue?: string;
  color: string;
  index: number;
  indexValue: string | number;
}

// Helper function to extract first name (ignoring titles like Mr., Mrs., Miss, etc.)
const extractFirstName = (fullName: string): string => {
  if (!fullName) return "";
  
  const nameWithoutTitle = fullName
    .replace(/^(Mr|Mrs|Miss|Ms|Dr|Prof|MISS|MR|MRS|MS|DR|PROF)\.?\s+/i, "")
    .trim();
  
  const firstName = nameWithoutTitle.split(" ")[0];
  return firstName;
};

const fullNamesMap = new Map<string, string>();

const formatBeneficiaryLabel = (value: string): string => {
  const firstName = extractFirstName(value);
  return firstName.length > 15 ? firstName.substring(0, 12) + "..." : firstName;
};

const getBeneficiaryFullName = (labelValue: string): string => {
  return fullNamesMap.get(labelValue) || labelValue;
};

// Custom tooltip for pie chart
const CustomPieTooltip = ({ datum }: { datum: any }) => (
  <div className="rounded-lg bg-neutral-800 px-3 py-2 text-white shadow-lg">
    <p className="text-sm font-medium">
      {datum.id}: {DEFAULT_CURRENCY_SYMBOL} {formatCurrency(datum.value, false)}
    </p>
  </div>
);

const SendMoneyBeneficiariesComponent = memo(
  function SendMoneyBeneficiariesComponent(
    props: SendMoneyBeneficiariesComponentProps
  ) {
    const { displayBeneficiaryData, displaySendMoneyData } = props;

    const barChartData: { beneficiary: string; amount: number }[] = [];
    const pieData: PieDataProps[] = [];

    // Build full names map for tooltips
    fullNamesMap.clear();
    displayBeneficiaryData?.forEach((entry) => {
      const fullName = entry.beneficiaryName as string;
      const firstName = extractFirstName(fullName);
      fullNamesMap.set(firstName, fullName);
      barChartData.push({
        beneficiary: fullName,
        amount: entry.amount as number,
      });
    });

    if (displaySendMoneyData && displaySendMoneyData.length) {
      let sendMoneyData = [...(displaySendMoneyData as SendMoneyChartDTO[])];

      if (displaySendMoneyData.length > 1) {
        sendMoneyData.sort((a, b) => {
          const amountA = a.amount as number;
          const amountB = b.amount as number;
          return amountB - amountA;
        });

        if (sendMoneyData.length > 9) {
          sendMoneyData = sendMoneyData.slice(0, 9);
        }

        sendMoneyData.sort((a, b) => {
          const categoryA = (a.target as string) || "Others";
          const categoryB = (b.target as string) || "Others";

          if (categoryA < categoryB) {
            return -1;
          }
          if (categoryA > categoryB) {
            return 1;
          }
          return 0;
        });
      }

      sendMoneyData?.forEach((item, idx) => {
        if ((item.amount as number) > 0) {
          pieData.push({
            id: item.target as string,
            label: (item.target as string) || "Others",
            value: item.amount as number,
            color: getChartEntryColor(idx),
            index: idx,
            indexValue: idx,
          });
        }
      });
    }

    const SendMoneyPieChart = (): JSX.Element => {
      return !pieData.length ? (
        <EmptyChartCardComponent />
      ) : (
        <div
          className="flex h-full w-full"
          style={{ height: "24rem", maxWidth: 500 }}
        >
          <ResponsivePie
            data={pieData}
            margin={{ right: 150, left: 10 }}
            innerRadius={0.5}
            cornerRadius={0}
            colors={{ datum: "data.color" }}
            theme={{
              fontFamily: "Outfit",
              tooltip: {
                container: {
                  background: "#373737",
                  color: "white",
                  borderRadius: "8px",
                  fontSize: "12px",
                  padding: "8px 12px",
                },
              },
            }}
            activeOuterRadiusOffset={8}
            borderWidth={1}
            borderColor={{
              from: "color",
              modifiers: [["darker", 0.1]],
            }}
            enableArcLabels={true}
            arcLabelsTextColor={"white"}
            arcLabelsSkipAngle={0}
            enableArcLinkLabels={false}
            arcLinkLabelsSkipAngle={2}
            arcLinkLabelsColor={{
              from: "color",
            }}
            valueFormat={(value) => getSuffixedNumber(value)}
            arcLinkLabelsThickness={1}
            arcLinkLabel={(d) =>
              d.value > 0 ? `${d.id}: ${getSuffixedNumber(d.value)}` : ""
            }
            tooltip={({ datum }) => <CustomPieTooltip datum={datum} />}
            legends={[
              {
                anchor: "right",
                direction: "column",
                justify: false,
                translateX: 150,
                itemsSpacing: 20,
                itemWidth: 100,
                itemHeight: 18,
                itemTextColor: "#1A1C1F",
                itemDirection: "left-to-right",
                itemOpacity: 1,
                symbolSize: 18,
                symbolShape: "circle",
              },
            ]}
          />
        </div>
      );
    };

    return (
      <div className="grid w-full grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="">
          <div className="mb-4 h-7 items-center justify-start pl-4 pr-2 text-xl font-medium leading-7 text-zinc-900">
            Send Money ({DEFAULT_CURRENCY_SYMBOL})
          </div>
          <div className="flex rounded-lg border border-zinc-300 bg-white p-8">
            <SendMoneyPieChart />
          </div>
        </div>

        <div className="">
          <div className="mb-4 h-7 items-center justify-start pl-4 pr-2 text-xl font-medium leading-7 text-zinc-900">
            Top 10 Beneficiaries ({DEFAULT_CURRENCY_SYMBOL})
          </div>
          <BarChartComponent
            data={barChartData.reverse()}
            indexBy={"beneficiary"}
            keys={["amount"]}
            layout="horizontal"
            margin={{ top: 50, right: 80, bottom: 50, left: 80 }}
            height={385}
            formatAxisLabel={formatBeneficiaryLabel}
            getTooltipFullName={getBeneficiaryFullName}
          />
        </div>
      </div>
    );
  }
);

export default SendMoneyBeneficiariesComponent;